import * as React from "react";
import { css } from "@emotion/core";
import { useDropzone } from "react-dropzone";
import { useSinkState } from "context/sink";
import { useUploadMachineSelector } from "../../context/upload-machine";
import {
  useImageDropzone,
  usePointCloudDropzone,
  useGeometryDropzone,
} from "../../hooks";
import {
  acceptedExtensions,
  acceptedImageFiles,
  acceptedPointcloudFiles,
  acceptedGeometryFiles,
} from "../../utils/fileAcceptance";
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";
import Uploads from "../Uploads";
import { GeometryMachineContext } from "../../state/geometryMachine";
import { LidarMachineContext } from "../../state/lidarMachine";
import { ImagesMachineContext } from "components/Orders/state/imagesMachine";
import { OrderMachineContext } from "components/Orders/state/orderMachine";
import Complete from "../Complete";
import ProcessProgress from "../ProcessProgress";
import UploadControls from "./UploadControls";
import ImagePreviewPanel from "./ImagePreviewPanel";
import MapView from "./MapView";
import UploadListsContainer from "./UploadListsContainer";
import ReviewPanelContainer from "./ReviewPanelContainer";
import UploadAction from "./UploadAction";
import { getCenter, centerMapToAssets } from "./utils";
import * as turf from "@turf/helpers";
import center from "@turf/center";
import {
  getFileCaptureTime,
  isFileOld,
  parseExifDate,
} from "../../utils/validation";
import { ProcessedImage, ProcessedFile, PointCloudData } from "../../state/types";

import "mapbox-gl/dist/mapbox-gl.css";

export default function SelectUploads() {
  const { sink } = useSinkState();

  const mapRef = React.useRef(null);
  const mapPanelRef = React.useRef(null);
  const resizeTimeoutRef = React.useRef<NodeJS.Timeout>();

  const debouncedResize = React.useMemo(() => {
    return () => {
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
      resizeTimeoutRef.current = setTimeout(() => {
        if (mapRef.current) {
          mapRef.current.resize();
        }
      }, 100);
    };
  }, []);

  const imagesMachine = ImagesMachineContext.useActorRef();
  const lidarMachine = LidarMachineContext.useActorRef();
  const geometryMachine = GeometryMachineContext.useActorRef();

  const imageUploads = ImagesMachineContext.useSelector(
    (state) => state.context.uploads
  );

  const lidarUploads = LidarMachineContext.useSelector(
    (state) => state.context.uploads
  );

  const geometryUploads = GeometryMachineContext.useSelector(
    (state) => state.context.uploads
  );

  const orderMachine = OrderMachineContext.useActorRef();

  const [latitude, longitude] = OrderMachineContext.useSelector((state) => {
    return [state.context.center?.[1] ?? "0", state.context.center?.[0] ?? "0"];
  });

  const zoom = OrderMachineContext.useSelector((state) => state.context.zoom);

  const uploadsInProgress = useUploadMachineSelector((state) => {
    return state.context.fileUploadActors.length > 0;
  });

  const uploadsComplete = useUploadMachineSelector((state) => {
    const {
      resume,
      totalBytes,
      uploadedBytes,
      fileUploadActors,
      queuedFiles,
      files,
    } = state.context;

    const noPendingUploads =
      fileUploadActors.length === 0 &&
      queuedFiles.length === 0 &&
      files.length === 0;

    const hasUploadedData = totalBytes > 0 || uploadedBytes > 0;

    return (
      resume &&
      hasUploadedData &&
      noPendingUploads &&
      uploadedBytes >= totalBytes
    );
  });

  const fileAgeMap = React.useMemo(() => {
    const map = new Map<string, boolean>();
    
    imageUploads.forEach((upload: ProcessedImage) => {
      const captureTime = getFileCaptureTime(upload);
      map.set(upload.id, isFileOld(captureTime));
    });
    
    lidarUploads.forEach((upload: ProcessedFile<PointCloudData>) => {
      const captureTime = getFileCaptureTime(upload);
      map.set(upload.id, isFileOld(captureTime));
    });
    
    return map;
  }, [imageUploads, lidarUploads]);

  const validImages = React.useMemo(() => {
    return imageUploads.filter((img) => img.data?.exif?.geoLocation);
  }, [imageUploads]);

  const imageCenter = React.useMemo(() => {
    if (validImages.length === 0) return null;

    const points = turf.featureCollection(
      validImages.map((img) =>
        turf.point([
          img.data.exif.geoLocation.longitude,
          img.data.exif.geoLocation.latitude,
        ])
      )
    );

    const [lon, lat] = center(points).geometry.coordinates;
    return { longitude: lon, latitude: lat };
  }, [validImages]);

  const imageTimestamps = React.useMemo(() => {
    if (validImages.length === 0) return null;

    const all = validImages
      .map((img) => parseExifDate(img.data.exif.dateTime))
      .filter((d): d is Date => d !== null)
      .sort((a, b) => a.getTime() - b.getTime());

    if (all.length === 0) return { start: null, end: null };
    return { start: all[0], end: all[all.length - 1] };
  }, [validImages]);

  const lidarWithBbox = React.useMemo(() => {
    return lidarUploads.filter((lidar) => lidar.data?.bbox);
  }, [lidarUploads]);

  const lidarCenter = React.useMemo(() => {
    if (lidarWithBbox.length === 0) return null;

    const allBboxes = lidarWithBbox.map((lidar) => lidar.data.bbox);
    const combinedBbox = allBboxes.reduce(
      (acc, bbox) => [
        Math.min(acc[0], bbox[0]),
        Math.min(acc[1], bbox[1]),
        Math.min(acc[2], bbox[2]),
        Math.max(acc[3], bbox[3]),
        Math.max(acc[4], bbox[4]),
        Math.max(acc[5], bbox[5]),
      ],
      allBboxes[0]
    );

    const centerX = (combinedBbox[0] + combinedBbox[3]) / 2;
    const centerY = (combinedBbox[1] + combinedBbox[4]) / 2;

    return { longitude: centerX, latitude: centerY };
  }, [lidarWithBbox]);

  const lidarTimestamps = React.useMemo(() => {
    const lidarWithTimestamps = lidarUploads.filter(
      (lidar) => lidar.data?.timestamps
    );

    if (lidarWithTimestamps.length === 0) return null;

    const allTimestamps = lidarWithTimestamps[0].data.timestamps.sort(
      (a, b) => a - b
    );

    const startTime = new Date(allTimestamps[0]);
    const endTime = new Date(allTimestamps[allTimestamps.length - 1]);

    return { start: startTime, end: endTime };
  }, [lidarUploads]);

  const customDetails = OrderMachineContext.useSelector(
    (state) => state.context.customDetails
  );

  React.useEffect(() => {
    if (customDetails) {
      return;
    }

    if (imageCenter && imageTimestamps) {
      orderMachine.send({
        type: "SET_CENTER",
        latitude: imageCenter.latitude.toFixed(6),
        longitude: imageCenter.longitude.toFixed(6),
      });

      orderMachine.send({
        type: "SET_START_TIME",
        startTime: imageTimestamps.start
          ? imageTimestamps.start.toISOString()
          : null,
      });

      orderMachine.send({
        type: "SET_END_TIME",
        endTime: imageTimestamps.end
          ? imageTimestamps.end.toISOString()
          : null,
      });
    } else {
      if (lidarCenter && validImages.length === 0) {
        orderMachine.send({
          type: "SET_CENTER",
          latitude: lidarCenter.latitude.toFixed(6),
          longitude: lidarCenter.longitude.toFixed(6),
        });

        orderMachine.send({
          type: "SET_START_TIME",
          startTime: lidarTimestamps
            ? lidarTimestamps.start.toISOString()
            : null,
        });

        orderMachine.send({
          type: "SET_END_TIME",
          endTime: lidarTimestamps ? lidarTimestamps.end.toISOString() : null,
        });
      }
    }
  }, [
    imageCenter,
    imageTimestamps,
    lidarCenter,
    lidarTimestamps,
    validImages.length,
    orderMachine,
    customDetails,
  ]);

  const selected = ImagesMachineContext.useSelector(
    (state) => state.context.selected.length > 0
  );

  const dropzone = useDropzone({
    accept: {
      ...acceptedExtensions.images,
      ...acceptedExtensions.pointclouds,
      ...acceptedExtensions.geometry,
    },
    onDrop: (acceptedFiles) => {
      if (acceptedFiles.length === 0) return;

      imagesMachine.send({
        type: "ADD_FILES",
        files: acceptedImageFiles(acceptedFiles),
      });

      lidarMachine.send({
        type: "ADD_FILES",
        files: acceptedPointcloudFiles(acceptedFiles),
      });

      geometryMachine.send({
        type: "ADD_FILES",
        files: acceptedGeometryFiles(acceptedFiles),
      });
    },
  });

  const imagesDropzone = useImageDropzone(imagesMachine);
  const lidarDropzone = usePointCloudDropzone(lidarMachine);
  const geometryDropzone = useGeometryDropzone(geometryMachine);

  const focusOnImage = React.useCallback(() => {
    if (mapRef.current && imageUploads.length > 0) {
      centerMapToAssets(mapRef.current, imageUploads, true);
    }
  }, [imageUploads]);

  React.useEffect(() => {
    const subscription = imagesMachine.on("focusOnImages", () => {
      if (validImages.length > 0 && mapRef.current) {
        centerMapToAssets(mapRef.current, validImages, true);
      }
    });

    return subscription.unsubscribe;
  }, [imagesMachine, validImages]);

  const setLocationToImagesCenter = React.useCallback(() => {
    if (imageCenter) {
      orderMachine.send({
        type: "SET_CENTER",
        latitude: imageCenter.latitude.toFixed(6),
        longitude: imageCenter.longitude.toFixed(6),
      });
    }

    if (mapRef.current && imageUploads.length > 0) {
      centerMapToAssets(mapRef.current, imageUploads, true);
    }
  }, [imageCenter, imageUploads, orderMachine]);

  const focusOnPointCloud = React.useCallback((bbox: number[]) => {
    if (mapRef.current && bbox && bbox.length >= 6) {
      mapRef.current.fitBounds(
        [
          [bbox[0], bbox[1]],
          [bbox[3], bbox[4]],
        ],
        {
          animate: true,
          duration: 1500,
          padding: 64,
          maxZoom: 18,
          pitch: 60,
        }
      );

      mapRef.current.resize();
    }
  }, []);

  React.useEffect(() => {
    const subscription = lidarMachine.on("focusOnLidar", (event) => {
      focusOnPointCloud(event.bbox);
    });

    return subscription.unsubscribe;
  }, [lidarMachine, focusOnPointCloud]);

  React.useEffect(() => {
    if (!mapRef.current) return;

    if (validImages.length > 0) {
      centerMapToAssets(mapRef.current, validImages, true);
      return;
    }

    if (lidarWithBbox.length > 0) {
      const allBboxes = lidarWithBbox.map((lidar) => lidar.data.bbox);
      const combinedBbox = allBboxes.reduce(
        (acc, bbox) => [
          Math.min(acc[0], bbox[0]),
          Math.min(acc[1], bbox[1]),
          Math.min(acc[2], bbox[2]),
          Math.max(acc[3], bbox[3]),
          Math.max(acc[4], bbox[4]),
          Math.max(acc[5], bbox[5]),
        ],
        allBboxes[0]
      );

      focusOnPointCloud(combinedBbox);
    }
  }, [validImages, lidarWithBbox, focusOnPointCloud]);

  const focusOnGeometry = React.useCallback((bbox: number[]) => {
    if (mapRef.current && bbox && bbox.length >= 4) {
      mapRef.current.fitBounds(
        [
          [bbox[0], bbox[1]],
          [bbox[2], bbox[3]],
        ],
        {
          animate: true,
          duration: 1500,
          padding: 64,
          maxZoom: 18,
        }
      );

      mapRef.current.resize();
    }
  }, []);

  React.useEffect(() => {
    if (
      imageUploads.length === 0 &&
      lidarUploads.length === 0 &&
      geometryUploads.length === 0
    ) {
      orderMachine.send({ type: "RESET" });
    }
  }, [imageUploads, lidarUploads, geometryUploads, orderMachine]);

  const [minWidth, setMinWidth] = React.useState(20);

  React.useEffect(() => {
    const handler = () => {
      setMinWidth((640 / (window.innerWidth - 48)) * 100);
    };

    window.addEventListener("resize", handler);

    handler();

    return () => {
      window.removeEventListener("resize", handler);
    };
  }, []);

  React.useEffect(() => {
    return () => {
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
    };
  }, []);

  const mapCenter = React.useMemo(() => {
    if (
      latitude &&
      longitude &&
      Number(latitude) !== 0 &&
      Number(longitude) !== 0
    ) {
      return [Number(longitude), Number(latitude)];
    }
    return (
      getCenter(imageUploads, lidarUploads) ?? [
        sink.organization?.location.coordinates[0],
        sink.organization?.location.coordinates[1],
      ]
    );
  }, [
    latitude,
    longitude,
    imageUploads,
    lidarUploads,
    sink.organization?.location.coordinates,
  ]);

  if (uploadsComplete) {
    return <Complete />;
  }

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        height: 100%;
        width: 100%;
        padding: 0 1rem 1rem 1rem;
      `}
    >
      <ProcessProgress
        size="small"
        current={lidarUploads.length === 0 && imageUploads.length === 0 ? 2 : 3}
      />
      <PanelGroup direction="horizontal">
        <Panel minSize={minWidth} order={1}>
          <div
            css={css`
              width: 100%;
              height: 100%;
              overflow: hidden;
              background-color: var(--neutral-7);
              border: 1px solid var(--neutral-5);
              border-top: 0;
              display: flex;
              flex-direction: column;
            `}
          >
            {uploadsInProgress ? null : (
              <UploadControls
                imagesDropzone={imagesDropzone}
                lidarDropzone={lidarDropzone}
                geometryDropzone={geometryDropzone}
                dropzone={dropzone}
                showDropzone={
                  imageUploads.length === 0 &&
                  lidarUploads.length === 0 &&
                  geometryUploads.length === 0
                }
              />
            )}
            <UploadListsContainer
              onFocusImage={focusOnImage}
              onFocusPointCloud={focusOnPointCloud}
              onFocusGeometry={focusOnGeometry}
              onSetLocationToCenter={setLocationToImagesCenter}
              uploadInProgress={uploadsInProgress}
              fileAgeMap={fileAgeMap}
            />
            <UploadAction fileAgeMap={fileAgeMap} />
          </div>
        </Panel>
        <PanelResizeHandle>
          <div
            css={css`
              color: var(--neutral-5);
              height: 100%;
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              padding: 0 5px;
            `}
          >
            <svg
              width="6"
              height="28"
              viewBox="0 0 6 28"
              fill="var(--neutral-5)"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect width="2" height="28" />
              <rect x="4" width="2" height="28" />
            </svg>
          </div>
        </PanelResizeHandle>
        {selected && uploadsInProgress === false && (
          <>
            <Panel
              minSize={20}
              order={2}
              onLoad={() => {
                if (mapRef.current) {
                  mapRef.current.resize();
                }
              }}
            >
              <ImagePreviewPanel imageUploads={imageUploads} />
            </Panel>
            <PanelResizeHandle>
              <div
                css={css`
                  color: var(--neutral-5);
                  height: 100%;
                  display: flex;
                  flex-direction: column;
                  align-items: center;
                  justify-content: center;
                  padding: 0 5px;
                `}
              >
                <svg
                  width="6"
                  height="28"
                  viewBox="0 0 6 28"
                  fill="var(--neutral-5)"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect width="2" height="28" />
                  <rect x="4" width="2" height="28" />
                </svg>
              </div>
            </PanelResizeHandle>
          </>
        )}
        {uploadsInProgress === false ? (
          <Panel minSize={20} order={3} onResize={debouncedResize}>
            <PanelGroup direction="vertical">
              <Panel ref={mapPanelRef} onResize={debouncedResize}>
                <MapView
                  mapRef={mapRef}
                  onZoomEnd={(event) => {
                    orderMachine.send({
                      type: "SET_ZOOM",
                      zoom: event.viewState.zoom,
                    });
                  }}
                  mapCenter={mapCenter as [number, number]}
                  zoom={
                    zoom ?? (imageUploads.length > 0 || lidarUploads.length > 0)
                      ? 14
                      : 8
                  }
                />
              </Panel>
              <ReviewPanelContainer mapRef={mapRef} />
            </PanelGroup>
          </Panel>
        ) : (
          <Panel minSize={30} order={3}>
            <Uploads />
          </Panel>
        )}
      </PanelGroup>
    </div>
  );
}
